import dayjs from 'dayjs';

import type { FilterField, FilterValues } from '../components/DataTable/DataTable.types';

/** Reads the value a field addresses out of an arbitrary record. */
export type FieldAccessor<T> = (record: T, key: string) => unknown;

export interface MatchOptions<T> {
    /** Defaults to indexing the record by the field key. */
    accessor?: FieldAccessor<T>;
    /**
     * Free-text search term, matched case-insensitively against `searchKeys`.
     * Kept separate from `values` because search is not a field: it spans
     * several columns and has no entry in `filterFields`.
     */
    search?: string;
    /** Which keys `search` looks at. No keys ⇒ search matches everything. */
    searchKeys?: string[];
}

const defaultAccessor: FieldAccessor<unknown> = (record, key) =>
    (record as Record<string, unknown>)?.[key];

/** Empty, null, `''`, or an all-null tuple means "this filter is not set". */
export function isFilterValueActive(value: unknown): boolean {
    if (value === null || value === undefined || value === '') return false;
    if (Array.isArray(value) && value.every((item) => item === null)) return false;
    if (Array.isArray(value) && value.length === 0) return false;
    return true;
}

/**
 * THE filter predicate — one implementation for every table surface in the kit.
 *
 * `DataTable` and `MatrixTable` both render `FilterField[]` controls and both
 * hold their state in a `FilterValues` bag, but they used to decide what a value
 * MEANT independently: `DataTable` left the predicate entirely to the caller,
 * and `MatrixTable` took a bespoke `filterRow` callback. Same props, two
 * behaviours — so "filter by group" worked one way on one screen and another way
 * on the next, and a `select` that accepted multiple values was a per-caller
 * accident rather than a property of the kit.
 *
 * The rules, once, here:
 *
 * - **`select`** — equality against the field value, coerced to string so
 *   `1` and `'1'` match. An ARRAY of chosen values is an OR (antd's own
 *   multi-select filter dropdown produces arrays), and if the RECORD's value is
 *   an array it matches when any element is chosen. Both directions matter: a
 *   permission belongs to one group, but a user can hold several roles.
 * - **`text`** — case-insensitive substring.
 * - **`date`** — same calendar day, not the same instant. Comparing timestamps
 *   makes a filter that can never match anything with a time component.
 * - **`dateRange`** — inclusive on both ends, and a half-open range (one bound
 *   null) filters only on the bound that is set.
 * - **An inactive value never excludes anything**, so an untouched filter is
 *   invisible rather than empty-matching.
 *
 * Unknown field types pass rather than fail: a table must not silently show
 * nothing because someone added a filter type the engine has not learned.
 */
export function matchesFilters<T>(
    record: T,
    fields: FilterField[],
    values: FilterValues,
    options: MatchOptions<T> = {}
): boolean {
    const accessor = (options.accessor ?? (defaultAccessor as FieldAccessor<T>)) as FieldAccessor<T>;

    const term = options.search?.trim().toLowerCase();
    if (term) {
        const keys = options.searchKeys ?? [];
        if (keys.length > 0) {
            const hit = keys.some((key) => {
                const raw = accessor(record, key);
                return raw !== null && raw !== undefined && String(raw).toLowerCase().includes(term);
            });
            if (!hit) return false;
        }
    }

    for (const field of fields) {
        const chosen = values[field.key];
        if (!isFilterValueActive(chosen)) continue;

        const actual = accessor(record, field.key);
        if (!matchesField(field, chosen, actual)) return false;
    }

    return true;
}

function matchesField(field: FilterField, chosen: unknown, actual: unknown): boolean {
    switch (field.type) {
        case 'select': {
            const wanted = (Array.isArray(chosen) ? chosen : [chosen])
                .filter((v) => v !== null && v !== undefined && v !== '')
                .map(String);
            if (wanted.length === 0) return true;
            const have = (Array.isArray(actual) ? actual : [actual])
                .filter((v) => v !== null && v !== undefined)
                .map(String);
            return have.some((v) => wanted.includes(v));
        }
        case 'text': {
            if (actual === null || actual === undefined) return false;
            return String(actual).toLowerCase().includes(String(chosen).toLowerCase());
        }
        case 'date': {
            if (!actual) return false;
            return dayjs(actual as string).isSame(dayjs(chosen as string), 'day');
        }
        case 'dateRange': {
            if (!Array.isArray(chosen)) return true;
            const [from, to] = chosen as [unknown, unknown];
            if (!actual) return false;
            const at = dayjs(actual as string);
            if (from && at.isBefore(dayjs(from as string), 'day')) return false;
            if (to && at.isAfter(dayjs(to as string), 'day')) return false;
            return true;
        }
        default:
            // Unknown type — do not silently empty the table.
            return true;
    }
}

/** `matchesFilters` over a list. */
export function applyFilters<T>(
    rows: T[],
    fields: FilterField[],
    values: FilterValues,
    options: MatchOptions<T> = {}
): T[] {
    if (fields.length === 0 && !options.search?.trim()) return rows;
    return rows.filter((row) => matchesFilters(row, fields, values, options));
}

/** How many filters are actually narrowing the result — the "Bộ lọc (n)" count. */
export function activeFilterCount(fields: FilterField[], values: FilterValues): number {
    return fields.reduce(
        (n, field) => (isFilterValueActive(values[field.key]) ? n + 1 : n),
        0
    );
}
